import {
  required,
  validEmail,
  minLength,
  validUrl,
  requiredRichText,
  validSubdomain,
  validCurp,
  validMailboxPassword,
  hasSequentialDigits,
} from 'services/validators'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { PROFILE_TEXT_LIMITS, validateProfileText } from './profileText'

export const validatorUser = values => {
  const errors = {}
  if (!required(values.firstName)) {
    errors.firstName = 'Nombre requerido'
  }
  if (!required(values.lastName)) {
    errors.lastName = 'Apellido requerido'
  }

  if (!required(values.telephone)) {
    errors.telephone = 'Teléfono requerido'
  } else if (!isValidPhoneNumber(values.telephone)) {
    errors.telephone = 'Teléfono inválido'
  }

  if (!required(values.about) || !requiredRichText(values.about)) {
    errors.about = 'Biografía requerida'
  } else {
    errors.about = validateProfileText(values.about, PROFILE_TEXT_LIMITS.about)
  }

  if (!required(values.birthday)) {
    errors.birthday = 'Fecha de nacimiento requerida'
  }

  if (!required(values.experience) || !requiredRichText(values.experience)) {
    errors.experience = 'Experiencia requerida'
  } else {
    errors.experience = validateProfileText(values.experience, PROFILE_TEXT_LIMITS.experience)
  }
  if (!!values.video && !validUrl(values.video)) {
    errors.video = 'URL inválida'
  }

  return errors
}

export const validatorNewUser = values => {
  const errors = {}
  if (!required(values.firstName)) {
    errors.firstName = 'Nombre requerido'
  }

  if (!required(values.lastName)) {
    errors.lastName = 'Apellido requerido'
  }

  if (!required(values.birthday)) {
    errors.birthday = 'Fecha de nacimiento requerida'
  }

  if (!required(values.brokerType)) {
    errors.about = 'Requerido'
  }

  if (!required(values.country)) {
    errors.country = 'País requerido'
  }

  if (!required(values.curp)) {
    errors.curp = 'CURP Requerida'
  } else if (!validCurp(values.curp)) {
    errors.curp = "CURP inválida"
  }

  if (values.isFullService) {
    if (!required(values.optionSubdomain)) {
      errors.optionSubdomain = 'Seleccione un subdominio'
    } else if (values.optionSubdomain === 'CUSTOM') {
      if (!required(values.customSubdomain)) {
        errors.customSubdomain = 'Especifique un subdominio'
      }

      if (!validSubdomain(values.customSubdomain)) {
        errors.customSubdomain = 'El subdominio no está permitido'
      }

      if (!required(values.subdomain)) {
        errors.subdomain = 'Verifique la disponibilidad del subdominio'
      }
    }
  }

  return errors
}

export const validatorPassword = values => {
  const errors = {}

  if (!required(values.oldPassword)) {
    errors.oldPassword = 'Password anterior requerido'
  }
  if (!required(values.newPassword)) {
    errors.newPassword = 'Nuevo password requerido'
  }

  return errors
}

// Generate last 10 years dynamically
const currentYear = new Date().getFullYear()
const RECENT_YEARS = Array.from({ length: 15 }, (_, i) => String(currentYear - 9 + i))

const STATIC_DISALLOWED_WORDS = [
  'iad', 'neximo', 'mexico', 'karim', 'nico',
  // Common weak passwords
  'password', 'contraseña', 'hola', 'qwerty', 'admin', 'welcome',
  // Dynamic years (last 10 years)
  ...RECENT_YEARS
]

/**
 * Unified password validator for both profile and mailbox passwords
 * @param {object} values
 * @param {string} values.newPassword
 * @param {string} values.confirmPassword
 * @param {object} props
 * @param {object} [props.userProfile]
 * @returns {{newPassword?: string, confirmPassword?: string}}
 */
export const unifiedPasswordValidator = (values, props = {}) => {
  const errors = {}
  const { userProfile } = props

  // Required check
  if (!required(values.newPassword)) {
    errors.newPassword = 'Nueva contraseña requerida'
  } else if (!validMailboxPassword(values.newPassword)) {
    // Regex validation (10+ chars, upper, lower, number, special)
    errors.newPassword = 'La contraseña no cumple con los requisitos'
  } else if (hasSequentialDigits(values.newPassword)) {
    // Sequential digits check
    errors.newPassword = 'La contraseña no puede contener secuencias numéricas (ej: 123, 321)'
  }

  // Disallowed words check
  if (!errors.newPassword && values.newPassword) {
    const disallowedWords = [...STATIC_DISALLOWED_WORDS]
    if (userProfile) {
      const { firstName, secondName, lastName, secondLastName, mailbox, mailboxDomain } = userProfile
      disallowedWords.push(firstName, secondName, lastName, secondLastName, mailbox, mailboxDomain)
    }

    for (const word of disallowedWords) {
      if (!word) continue
      if (values.newPassword.toLowerCase().includes(word.toLowerCase())) {
        if (STATIC_DISALLOWED_WORDS.includes(word)) {
          errors.newPassword = `La contraseña no puede contener la palabra reservada "${word}"`
        } else {
          errors.newPassword = 'La contraseña no puede contener tu nombre, apellido o correo electrónico'
        }
        break
      }
    }
  }

  // Confirm password check
  if (!required(values.confirmPassword)) {
    errors.confirmPassword = 'Confirmar contraseña requerida'
  } else if (values.newPassword !== values.confirmPassword) {
    errors.confirmPassword = 'Las contraseñas no coinciden'
  }

  return errors
}

// Aliases for backward compatibility
export const profileNewPasswordValidator = unifiedPasswordValidator
export const mailboxNewPasswordValidator = unifiedPasswordValidator

export const ValidatorFiscalData = values => {
  const errors = {}
  if (!required(values.ine)) {
    errors.ine = 'Campo requerido'
  }
  if (!required(values.curp)) {
    errors.curp = 'CURP requerida'
  } else if (!validCurp(values.curp)) {
    errors.curp = "CURP inválida"
  }
  if (!required(values.bank)) {
    errors.bank = 'Campo requerido'
  }
  if (!required(values.bankAccount)) {
    errors.bankAccount = 'Campo requerido'
  }
  if (values.bankAccount && !minLength(values.bankAccount, 18)) {
    errors.bankAccount = 'La CLABE debe ser de 18 dígitos'
  }

  return errors
}


export const validatorSocialNetworks = values => {
  const errors = {}

  if (values.socialnetworks) {
    let socialsArrayErrors = []

    values.socialnetworks.forEach((item, index) => {
      if (item.url && !validUrl(item.url)) {
        socialsArrayErrors[index] = { url: 'Url inválida' }
      }
    })

    if (socialsArrayErrors.length) {
      errors.socialnetworks = socialsArrayErrors
    }
  }

  return errors
}

export const updateEmailValidator = values => {
  const errors = {}
  
  if (!values.emailUsername) {
    errors.emailUsername = 'Este campo es requerido'
  } else if (values.emailUsername.length < 3) {
    errors.emailUsername = 'El email debe tener al menos 3 caracteres'
  } else if (!/^[a-zA-Z0-9._-]+$/.test(values.emailUsername)) {
    errors.emailUsername = 'Solo se permiten letras, números, puntos, guiones y guiones bajos'
  }
  
  if (!values.emailDomain) {
    errors.emailDomain = 'El dominio es requerido'
  }
  
  return errors
}
